import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserClock, faPlus, faTrash, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPPORT_NAV } from './nav';

interface Shift {
    id: number; internal_user: string; internal_user_id: number; department: string;
    starts_at: string; ends_at: string; notes: string; is_current: boolean;
}
interface Agent { id: number; full_name: string; department: string; }

const OnCallRota: React.FC = () => {
    const { api } = useInternalApi();
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [agents, setAgents] = useState<Agent[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ internal_user_id: '', department: 'support', starts_at: '', ends_at: '', notes: '' });

    useEffect(() => { document.title = 'Escalation & On-Call - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('admin-dashboard/support/on-call/');
            if (res.data?.status === 'success') setShifts(res.data.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        api.get('admin-dashboard/support/agents/').then(r => { if (r.data?.status === 'success') setAgents(r.data.data || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitCreate = async () => {
        if (!form.internal_user_id || !form.starts_at || !form.ends_at) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Agent, start and end are required.' });
            return;
        }
        try {
            const res = await api.post('admin-dashboard/support/on-call/', form);
            if (res.data?.status === 'success') {
                setShowCreate(false);
                setForm({ internal_user_id: '', department: 'support', starts_at: '', ends_at: '', notes: '' });
                load();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not schedule shift.' });
        }
    };

    const removeShift = async (s: Shift) => {
        const confirm = await Swal.fire({ title: 'Remove this shift?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#f06548' });
        if (!confirm.isConfirmed) return;
        try {
            await api.delete(`admin-dashboard/support/on-call/${s.id}/`);
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not remove shift.' });
        }
    };

    const current = shifts.filter(s => s.is_current);

    return (
        <InternalShell activeDashboard="support_center" subNav={SUPPORT_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faUserClock} className="me-2 text-primary" />Escalation & On-Call</h2>
                    <p className="text-muted mb-0">Who is covering support escalations, and when</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />Schedule Shift</button>
            </div>

            <div className="alert alert-secondary small">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                This rota is the source of truth for who's covering. Escalating a ticket is a deliberate action taken in the Ticket Workbench — nothing auto-pages the on-call agent, since there's no background scheduler running to do so.
            </div>

            {current.length > 0 && (
                <div className="card border-0 shadow-sm mb-4 border-start border-4 border-success">
                    <div className="card-body">
                        <div className="text-muted small mb-1">On call right now</div>
                        {current.map(s => (
                            <div key={s.id}><strong>{s.internal_user}</strong> <span className="text-muted">({s.department})</span> — until {new Date(s.ends_at).toLocaleString()}</div>
                        ))}
                    </div>
                </div>
            )}

            <div className="card border-0 shadow-sm">
                <div className="card-header bg-white"><strong>Schedule</strong></div>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : shifts.length === 0 ? (
                        <div className="text-center py-5 text-muted">No shifts scheduled.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Agent</th><th>Department</th><th>Starts</th><th>Ends</th><th>Notes</th><th></th></tr></thead>
                            <tbody>
                                {shifts.map(s => (
                                    <tr key={s.id} className={s.is_current ? 'table-success' : ''}>
                                        <td>{s.internal_user}{s.is_current && <span className="badge bg-success ms-2">Now</span>}</td>
                                        <td>{s.department}</td>
                                        <td className="small">{new Date(s.starts_at).toLocaleString()}</td>
                                        <td className="small">{new Date(s.ends_at).toLocaleString()}</td>
                                        <td className="small">{s.notes || '—'}</td>
                                        <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => removeShift(s)}><FontAwesomeIcon icon={faTrash} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Schedule On-Call Shift</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Agent</label>
                                    <select className="form-select" value={form.internal_user_id} onChange={(e) => setForm(f => ({ ...f, internal_user_id: e.target.value }))}>
                                        <option value="">Select…</option>
                                        {agents.map(a => <option key={a.id} value={a.id}>{a.full_name} ({a.department})</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Department</label>
                                    <input className="form-control" value={form.department} onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))} />
                                </div>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <label className="form-label">Starts</label>
                                        <input type="datetime-local" className="form-control" value={form.starts_at} onChange={(e) => setForm(f => ({ ...f, starts_at: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Ends</label>
                                        <input type="datetime-local" className="form-control" value={form.ends_at} onChange={(e) => setForm(f => ({ ...f, ends_at: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <label className="form-label">Notes</label>
                                    <input className="form-control" value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Schedule</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default OnCallRota;
